import { Auth0Client, createAuth0Client, User } from "@auth0/auth0-spa-js"
import { IAuthProvider } from "./IAuthProvider"

const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN?.toString() || ""
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID?.toString() || ""
const auth0Audience = import.meta.env.VITE_AUTH0_AUDIENCE?.toString() || ""
const environmentURL = import.meta.env.VITE_BASE_URL?.toString() || window.location.origin

let auth0Client: Auth0Client | null = null
let tokenRenewedCallbacks: Array<(key: string, token: string) => void> = []
let tokenExpiredCallbacks: Array<(key: string) => void> = []
let errorCallbacks: Array<(error: Error) => void> = []

/**
 * Initializes the Auth0 client if not already initialized
 */
async function getAuth0Client(): Promise<Auth0Client> {
	if (!auth0Client) {
		auth0Client = await createAuth0Client({
			domain: auth0Domain,
			clientId: auth0ClientId,
			authorizationParams: {
				redirect_uri: `${environmentURL}/login/callback`,
				audience: auth0Audience,
				scope: "openid profile email offline_access",
			},
			cacheLocation: "localstorage",
			useRefreshTokens: true,
		})
	}
	return auth0Client
}

/**
 * Auth0 implementation of the IAuthProvider interface
 */
class Auth0AuthProvider implements IAuthProvider {
	readonly providerName = "Auth0"

	async isAuthenticated(): Promise<boolean> {
		try {
			const client = await getAuth0Client()
			return await client.isAuthenticated()
		} catch (error) {
			console.error("Auth0 isAuthenticated error:", error)
			return false
		}
	}

	async getAccessToken(): Promise<string | undefined> {
		try {
			const client = await getAuth0Client()
			const token = await client.getTokenSilently()
			return token
		} catch (error) {
			console.error("Auth0 getAccessToken error:", error)
			// Notify error callbacks
			errorCallbacks.forEach((cb) => cb(error as Error))
			return undefined
		}
	}

	async getIdToken(): Promise<string | undefined> {
		try {
			const client = await getAuth0Client()
			const claims = await client.getIdTokenClaims()
			return claims?.__raw
		} catch (error) {
			console.error("Auth0 getIdToken error:", error)
			return undefined
		}
	}

	isLoginRedirect(): boolean {
		const params = new URLSearchParams(window.location.search)
		return params.has("code") && params.has("state")
	}

	async signInWithRedirect(): Promise<void> {
		try {
			const client = await getAuth0Client()
			await client.loginWithRedirect({
				authorizationParams: {
					redirect_uri: `${environmentURL}/login/callback`,
				},
			})
		} catch (error) {
			console.error("Auth0 signInWithRedirect error:", error)
			throw error
		}
	}

	async handleCallback(): Promise<{ accessToken?: string; idToken?: string }> {
		try {
			const client = await getAuth0Client()

			// Handle the redirect callback
			const result = await client.handleRedirectCallback()
			console.log("Auth0 handleCallback result:", result)

			// Get the tokens
			const accessToken = await this.getAccessToken()
			const idToken = await this.getIdToken()

			// Store the access token in localStorage for cross-tab support
			if (accessToken) {
				localStorage.setItem("token", accessToken)
				// Notify token renewed callbacks
				tokenRenewedCallbacks.forEach((cb) => cb("accessToken", accessToken))
			}

			return { accessToken, idToken }
		} catch (error) {
			console.error("Auth0 handleCallback error:", error)
			throw error
		}
	}

	async signOut(): Promise<void> {
		try {
			const client = await getAuth0Client()
			await client.logout({
				logoutParams: {
					returnTo: window.location.origin,
				},
			})
			// Clear local storage
			this.clearTokens()
		} catch (error) {
			console.error("Auth0 signOut error:", error)
			throw error
		}
	}

	async start(): Promise<void> {
		try {
			// Initialize the Auth0 client — token renewal is handled automatically
			// by the SDK via useRefreshTokens: true and cacheLocation: "localstorage"
			await getAuth0Client()
		} catch (error) {
			console.error("Failed to start Auth0 Auth service:", error)
			throw error
		}
	}

	onTokenRenewed(callback: (key: string, token: string) => void): void {
		tokenRenewedCallbacks.push(callback)
	}

	onTokenExpired(callback: (key: string) => void): void {
		tokenExpiredCallbacks.push(callback)
	}

	onError(callback: (error: Error) => void): void {
		errorCallbacks.push(callback)
	}

	clearTokens(): void {
		localStorage.removeItem("token")
		localStorage.removeItem("PersonSessionData")
		sessionStorage.clear()
	}

	async renewToken(): Promise<string | undefined> {
		try {
			const client = await getAuth0Client()
			const token = await client.getTokenSilently({ cacheMode: "off" })
			if (token) {
				localStorage.setItem("token", token)
				tokenRenewedCallbacks.forEach((cb) => cb("accessToken", token))
			}
			return token
		} catch (error) {
			console.error("Auth0 renewToken error:", error)
			errorCallbacks.forEach((cb) => cb(error as Error))
			return undefined
		}
	}
}

// Export singleton instance
export const auth0Auth = new Auth0AuthProvider()
